import ConfirmationModal from "components/ConfirmationModal";
import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { ChangeEvent, useRef, useState } from "react";
import {
  addAppealCouncilLitigation,
  addAppealLitigation,
  editComplaint,
} from "services/prosecutorDecipilineService";
import { Appeal, Complaints, Litigation, Summon } from "types";
import { LoadingIcon } from "utils/constant";
import { APPEAL_APPOINTMENT_REASON } from "utils/constantValues";
import { ROLES } from "utils/roles";
import "../../complaintRequest/styles/AddEvidenceModal.css";
import { useTranslation } from "contexts/i18nContext";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: Summon;
  appealData: Appeal;
  complaintData: Complaints;
  onLitigationRefresh: any;
  onCouncilLitigationRefresh: any;
}

export default function AddAppealDailyHearingModal({
  isOpen,
  setIsOpen,
  summonData,
  appealData,
  complaintData,
  onLitigationRefresh,
  onCouncilLitigationRefresh,
}: ModalProps) {
  const { t } = useTranslation();
  const canCreateInspectionDailyHearing = useHasRole([
    ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canCreateCouncilDailyHearing = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);
  const { showToast } = useToast();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);

  const [formState, setFormState] = useState<Litigation>({
    is_final_verdict_given: false,
    hearing_date: "",
    next_appointment_date: "",
    given_order: "",
    appointment_reason: "",
    daily_report_summary: "",
    summon: null,
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;
    setFormState((prev) => ({
      ...prev,
      [name]:
        name === "is_final_verdict_given" || name === "prosecutor_opinion_type"
          ? checked
          : value,
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles([...Array.from(e.target.files as FileList)]);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!summonData && !appealData) {
      showToast(t("canNotFindSummonOrAppealData"), "error");
      return;
    }

    if (
      !showConfirmationModal &&
      formState.appointment_reason === APPEAL_APPOINTMENT_REASON.FINAL_DECISION
    ) {
      setShowConfirmationModal(true);
      return;
    }

    setLoading(true);

    // const data = {
    //   ...formState,
    //   is_final_verdict_given:
    //     formState?.appointment_reason ===
    //     APPEAL_APPOINTMENT_REASON.FINAL_DECISION
    //       ? true
    //       : false,
    //   summon: summonData?.id || "",
    // };

    const data = new FormData();
    data.append("hearing_date", formState.hearing_date);
    data.append("next_appointment_date", formState.next_appointment_date);
    data.append("given_order", formState.given_order || "");
    data.append("appointment_reason", formState.appointment_reason);
    data.append("daily_report_summary", formState.daily_report_summary);
    data.append("summon", summonData?.id || "");
    data.append("appeal_request", appealData?.id || "");
    data.append("meeting_file", files[0] || "");
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_assigned_prosecutor || "",
    );
    data.append(
      "accused_prosecutor_name",
      complaintData?.accused_prosecutor_name || "",
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "accused_prosecutor_org_id",
      complaintData?.accused_prosecutor_org_id || "",
    );
    data.append(
      "accused_prosecutor_admin_unit_id",
      complaintData?.accused_prosecutor_admin_unit_id || "",
    );
    data.append(
      "is_final_verdict_given",
      formState.appointment_reason === APPEAL_APPOINTMENT_REASON.FINAL_DECISION
        ? "true"
        : "false",
    );

    const isFormData = true;

    try {
      const api = canCreateInspectionDailyHearing
        ? addAppealLitigation
        : canCreateCouncilDailyHearing
          ? addAppealCouncilLitigation
          : null;
      if (!api) {
        showToast(t("noPermissionToCreateDailyHearing"), "error");
        return;
      }

      const resData = await api(data, isFormData);

      if (resData) {
        await editComplaint({
          ...complaintData,
          has_councileappeal: true,
          // status: COMPLIANT_STATUS.UNDER_LITIGATION,
        });
      }

      onLitigationRefresh();
      onCouncilLitigationRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        is_final_verdict_given: false,
        hearing_date: "",
        next_appointment_date: "",
        appointment_reason: "",
        daily_report_summary: "",
        summon: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);

    // const data = { ...formState, summon: summonData?.id || "" };

    const data = new FormData();
    data.append("hearing_date", formState.hearing_date);
    data.append("next_appointment_date", formState.next_appointment_date);
    data.append("given_order", formState.given_order || "");
    data.append("appointment_reason", formState.appointment_reason);
    data.append("daily_report_summary", formState.daily_report_summary);
    data.append("summon", summonData?.id || "");
    data.append("appeal_request", appealData?.id || "");
    data.append("meeting_files", files[0] || "");
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_assigned_prosecutor || "",
    );
    data.append(
      "accused_prosecutor_name",
      complaintData?.accused_prosecutor_name || "",
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "accused_prosecutor_org_id",
      complaintData?.accused_prosecutor_org_id || "",
    );
    data.append(
      "accused_prosecutor_admin_unit_id",
      complaintData?.accused_prosecutor_admin_unit_id || "",
    );
    data.append(
      "is_final_verdict_given",
      formState.appointment_reason === APPEAL_APPOINTMENT_REASON.FINAL_DECISION
        ? "true"
        : "false",
    );

    const isFormData = true;

    try {
      const api = canCreateInspectionDailyHearing
        ? addAppealLitigation
        : canCreateCouncilDailyHearing
          ? addAppealCouncilLitigation
          : null;

      if (!api) {
        showToast(t("noPermissionToCreateDailyHearing"), "error");
        return;
      }

      await api(data, isFormData);

      onLitigationRefresh();
      onCouncilLitigationRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        is_final_verdict_given: false,
        hearing_date: "",
        next_appointment_date: "",
        appointment_reason: "",
        daily_report_summary: "",
        summon: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("addAppealDailyHearing")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("hearingDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="hearing_date"
              placeholder={t("egJune122014")}
              value={formState.hearing_date}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("nextAppointmentDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type="date"
              name="next_appointment_date"
              placeholder={t("egJune122014")}
              value={formState.next_appointment_date}
              onChange={handleChange}
              min={
                formState.hearing_date
                  ? new Date(
                      new Date(formState.hearing_date).getTime() + 86400000,
                    )
                      .toISOString()
                      .split("T")[0] // strictly after hearing_date
                  : new Date().toISOString().split("T")[0] // fallback to today
              }
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("givenOrder")}
            </label>
            <textarea
              rows={3}
              name="given_order"
              value={formState.given_order}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("appointmentReason")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="appointment_reason"
              value={formState.appointment_reason ?? ""}
              onChange={handleChange}
            >
              {Object.values(APPEAL_APPOINTMENT_REASON)?.map((list) => (
                <option key={list} value={list}>
                  {list}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("dailyReportSummary")}
            </label>
            <textarea
              rows={3}
              name="daily_report_summary"
              value={formState.daily_report_summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>

          {/* <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Report Type
            </label>

            <div className="flex items-center gap-2 bg-gray-100 border p-2 rounded-md">
              <input
                className="form-checkbox h-4 w-4 text-blue-600"
                type="checkbox"
                name="is_final_verdict_given"
                checked={formState.is_final_verdict_given}
                onChange={handleChange}
              />
              <span className="text-sm text-gray-700">
                Is it final decision?
              </span>
            </div>
          </div> */}
        </section>

        {canCreateCouncilDailyHearing && (
          <section className="evidence-section">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("minuteFile")}
            </label>
            <div className="file-upload-area" onClick={triggerFileInput}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                // multiple
                style={{ display: "none" }}
              />
              <p className="upload-text">{t("uploadFiles")}</p>
              {files && files?.length > 0 && (
                <div className="file-list">
                  {files?.map((file, index) => (
                    <div key={index} className="file-item">
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}...
            </div>
          ) : (
            t("addDailyHearing")
          )}
        </button>
      </form>

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirm}
        isLoading={loading}
      />
    </SideModal>
  );
}
